/**
 * The browse methods (`listDirectory / listDatabases / listSchemas / listTables`) are reachable
 * before the execution socket has connected. Answering `[]` there is indistinguishable from a
 * genuinely empty folder, so the pane paints "nothing here" and never corrects itself. This holds
 * the caller until the client exists instead, and rejects if it never does — an error the surface
 * can show beats a folder that lies.
 */
import { useLatestRef, useOnUnmount, usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useEffect, useRef } from 'react';

export const SESSION_NOT_READY = 'The connection session is not available right now.';

const DEFAULT_TIMEOUT_MS = 30_000;

interface Waiter<T> {
  settle(ready: T): void;
  abandon(): void;
}

export function useSessionGate<T>(client: T | null | undefined, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const latest = useLatestRef(client);
  const waiters = useRef<Waiter<T>[]>([]);

  useEffect(() => {
    if (!client) return;
    const pending = waiters.current;
    waiters.current = [];
    for (const waiter of pending) waiter.settle(client);
  }, [client]);

  // Nothing resolves a waiter once the effect is gone, so an unmounted gate would hold its callers
  // for the whole timeout before failing them.
  useOnUnmount(() => {
    const pending = waiters.current;
    waiters.current = [];
    for (const waiter of pending) waiter.abandon();
  });

  // A reconnect replaces the client rather than failing the wait: pending callers keep waiting for
  // the next one, bounded by their own timeout.
  return usePersistentCallback(
    () =>
      new Promise<T>((resolve, reject) => {
        if (latest.current) {
          resolve(latest.current);
          return;
        }

        let timer = 0;
        const waiter: Waiter<T> = {
          settle: (ready) => {
            window.clearTimeout(timer);
            resolve(ready);
          },
          abandon: () => {
            window.clearTimeout(timer);
            reject(new Error(SESSION_NOT_READY));
          }
        };
        timer = window.setTimeout(() => {
          waiters.current = waiters.current.filter((candidate) => candidate !== waiter);
          reject(new Error(SESSION_NOT_READY));
        }, timeoutMs);
        waiters.current.push(waiter);
      })
  );
}
